import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  BannerConfig,
  FAQConfig,
  TestimonialConfig,
  AdminBanner,
  AdminFAQ,
  AdminTestimonial,
  getBannerConfig,
  getFAQConfig,
  getTestimonialConfig,
  updateBannerConfig,
  updateFAQConfig,
  updateTestimonialConfig,
  subscribeToBannerConfig,
  subscribeToFAQConfig,
  subscribeToTestimonialConfig
} from '../services/contentService';
import {
  LaunchSplashImage,
  createLaunchSplashImage,
  deleteLaunchSplashImage,
  getLaunchSplashImages,
  setLaunchSplashImageStatus,
  subscribeToLaunchSplashImages,
} from '../services/launchSplashService';

// Banner Hook (moved from useBanners)
interface UseBannersResult {
  banners: AdminBanner[];
  bannerConfig: BannerConfig;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateBanner: (bannerId: string, bannerData: Partial<AdminBanner>) => Promise<void>;
  addBanner: (bannerData: Omit<AdminBanner, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

export const useBanners = (): UseBannersResult => {
  const [bannerConfig, setBannerConfig] = useState<BannerConfig>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Convert banner config to array format for easier rendering
  const banners: AdminBanner[] = [];
  if (bannerConfig.banner1) banners.push(bannerConfig.banner1);
  if (bannerConfig.banner2) banners.push(bannerConfig.banner2);

  // Initial load and real-time subscription
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initializeBanners = async () => {
      try {
        setLoading(true);
        setError(null);

        // Subscribe to real-time updates
        unsubscribe = subscribeToBannerConfig((config) => {
          setBannerConfig(config);
          setLoading(false);
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load banners';
        setError(errorMessage);
        setLoading(false);
        console.error('Error initializing banners:', err);
      }
    };

    initializeBanners();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Refresh function to manually reload banner data
  const refresh = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const config = await getBannerConfig();
      setBannerConfig(config);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh banners';
      setError(errorMessage);
      console.error('Error refreshing banners:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update banner function
  const updateBanner = async (bannerId: string, bannerData: Partial<AdminBanner>): Promise<void> => {
    try {
      const updatedConfig = { ...bannerConfig };
      
      // Check if we're trying to activate this banner
      const isActivatingBanner = bannerData.isActive === true;
      
      // If activating, check how many banners are currently active
      if (isActivatingBanner) {
        const activeBanners = [];
        if (bannerConfig.banner1?.isActive) activeBanners.push('banner1');
        if (bannerConfig.banner2?.isActive) activeBanners.push('banner2');
        
        // If we already have 2 active banners and we're not editing one of them
        if (activeBanners.length >= 2 && !activeBanners.includes(bannerId)) {
          // Deactivate the current bottom banner (banner2)
          if (updatedConfig.banner2) {
            updatedConfig.banner2 = {
              ...updatedConfig.banner2,
              isActive: false,
              updatedAt: new Date()
            };
            toast.info('Previous bottom banner has been automatically deactivated to maintain the 2-banner limit.');
          }
        }
      }
      
      if (bannerId === 'banner1') {
        updatedConfig.banner1 = {
          ...bannerConfig.banner1,
          ...bannerData,
          id: 'banner1',
          order: 1,
          updatedAt: new Date()
        } as AdminBanner;
      } else if (bannerId === 'banner2') {
        updatedConfig.banner2 = {
          ...bannerConfig.banner2,
          ...bannerData,
          id: 'banner2',
          order: 2,
          updatedAt: new Date()
        } as AdminBanner;
      }

      await updateBannerConfig(updatedConfig);
      // The real-time subscription will automatically update the local state
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update banner';
      setError(errorMessage);
      console.error('Error updating banner:', err);
      throw err;
    }
  };

  // Add banner function
  const addBanner = async (bannerData: Omit<AdminBanner, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
      const updatedConfig = { ...bannerConfig };
      const bannerId = bannerData.order === 1 ? 'banner1' : 'banner2';
      
      // Check if we're trying to create an active banner
      const isActiveBanner = bannerData.isActive === true;
      
      // If creating an active banner, check how many banners are currently active
      if (isActiveBanner) {
        const activeBanners = [];
        if (bannerConfig.banner1?.isActive) activeBanners.push('banner1');
        if (bannerConfig.banner2?.isActive) activeBanners.push('banner2');
        
        // If we already have 2 active banners
        if (activeBanners.length >= 2) {
          // Deactivate the current bottom banner (banner2)
          if (updatedConfig.banner2) {
            updatedConfig.banner2 = {
              ...updatedConfig.banner2,
              isActive: false,
              updatedAt: new Date()
            };
            toast.info('Previous bottom banner has been automatically deactivated to maintain the 2-banner limit.');
          }
          
          // Force the new banner to be banner2 (bottom position) regardless of original order
          const newBanner: AdminBanner = {
            ...bannerData,
            id: 'banner2',
            order: 2,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          updatedConfig.banner2 = newBanner;
        } else {
          // Normal creation when we don't have 2 active banners
          const newBanner: AdminBanner = {
            ...bannerData,
            id: bannerId,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          if (bannerId === 'banner1') {
            updatedConfig.banner1 = newBanner;
          } else {
            updatedConfig.banner2 = newBanner;
          }
        }
      } else {
        // Normal creation for inactive banner
        const newBanner: AdminBanner = {
          ...bannerData,
          id: bannerId,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        if (bannerId === 'banner1') {
          updatedConfig.banner1 = newBanner;
        } else {
          updatedConfig.banner2 = newBanner;
        }
      }

      await updateBannerConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add banner';
      setError(errorMessage);
      console.error('Error adding banner:', err);
      throw err;
    }
  };

  return {
    banners,
    bannerConfig,
    loading,
    error,
    refresh,
    updateBanner,
    addBanner
  };
};

// FAQ Hook
interface UseFAQsResult {
  faqs: AdminFAQ[];
  faqConfig: FAQConfig;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateFAQ: (faqId: string, faqData: Partial<AdminFAQ>) => Promise<void>;
  addFAQ: (faqData: Omit<AdminFAQ, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  deleteFAQ: (faqId: string) => Promise<void>;
}

export const useFAQs = (): UseFAQsResult => {
  const [faqConfig, setFAQConfig] = useState<FAQConfig>({ faqs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Convert FAQ config to array format for easier rendering
  const faqs = faqConfig.faqs || [];

  // Initial load and real-time subscription
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initializeFAQs = async () => {
      try {
        setLoading(true);
        setError(null);

        // Subscribe to real-time updates
        unsubscribe = subscribeToFAQConfig((config) => {
          setFAQConfig(config);
          setLoading(false);
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load FAQs';
        setError(errorMessage);
        setLoading(false);
        console.error('Error initializing FAQs:', err);
      }
    };

    initializeFAQs();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Refresh function to manually reload FAQ data
  const refresh = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const config = await getFAQConfig();
      setFAQConfig(config);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh FAQs';
      setError(errorMessage);
      console.error('Error refreshing FAQs:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update FAQ function
  const updateFAQ = async (faqId: string, faqData: Partial<AdminFAQ>): Promise<void> => {
    try {
      const updatedFAQs = faqs.map(faq => 
        faq.id === faqId 
          ? { ...faq, ...faqData, updatedAt: new Date() }
          : faq
      );

      const updatedConfig = { ...faqConfig, faqs: updatedFAQs };
      await updateFAQConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update FAQ';
      setError(errorMessage);
      console.error('Error updating FAQ:', err);
      throw err;
    }
  };

  // Add FAQ function
  const addFAQ = async (faqData: Omit<AdminFAQ, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
      const newFAQ: AdminFAQ = {
        ...faqData,
        id: `faq-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const updatedFAQs = [...faqs, newFAQ];
      const updatedConfig = { ...faqConfig, faqs: updatedFAQs };
      await updateFAQConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add FAQ';
      setError(errorMessage);
      console.error('Error adding FAQ:', err);
      throw err;
    }
  };

  // Delete FAQ function
  const deleteFAQ = async (faqId: string): Promise<void> => {
    try {
      const updatedFAQs = faqs.filter(faq => faq.id !== faqId);
      const updatedConfig = { ...faqConfig, faqs: updatedFAQs };
      await updateFAQConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete FAQ';
      setError(errorMessage);
      console.error('Error deleting FAQ:', err);
      throw err;
    }
  };

  return {
    faqs,
    faqConfig,
    loading,
    error,
    refresh,
    updateFAQ,
    addFAQ,
    deleteFAQ
  };
};

// Testimonial Hook
interface UseTestimonialsResult {
  testimonials: AdminTestimonial[];
  testimonialConfig: TestimonialConfig;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateTestimonial: (testimonialId: string, testimonialData: Partial<AdminTestimonial>) => Promise<void>;
  addTestimonial: (testimonialData: Omit<AdminTestimonial, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  deleteTestimonial: (testimonialId: string) => Promise<void>;
}

export const useTestimonials = (): UseTestimonialsResult => {
  const [testimonialConfig, setTestimonialConfig] = useState<TestimonialConfig>({ testimonials: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Convert testimonial config to array format for easier rendering
  const testimonials = testimonialConfig.testimonials || [];

  // Initial load and real-time subscription
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initializeTestimonials = async () => {
      try {
        setLoading(true);
        setError(null);

        // Subscribe to real-time updates
        unsubscribe = subscribeToTestimonialConfig((config) => {
          setTestimonialConfig(config);
          setLoading(false);
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load testimonials';
        setError(errorMessage);
        setLoading(false);
        console.error('Error initializing testimonials:', err);
      }
    };

    initializeTestimonials();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Refresh function to manually reload testimonial data
  const refresh = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const config = await getTestimonialConfig();
      setTestimonialConfig(config);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh testimonials';
      setError(errorMessage);
      console.error('Error refreshing testimonials:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update testimonial function
  const updateTestimonial = async (testimonialId: string, testimonialData: Partial<AdminTestimonial>): Promise<void> => {
    try {
      const updatedTestimonials = testimonials.map(testimonial => 
        testimonial.id === testimonialId 
          ? { ...testimonial, ...testimonialData, updatedAt: new Date() }
          : testimonial
      );

      const updatedConfig = { ...testimonialConfig, testimonials: updatedTestimonials };
      await updateTestimonialConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update testimonial';
      setError(errorMessage);
      console.error('Error updating testimonial:', err);
      throw err;
    }
  };

  // Add testimonial function
  const addTestimonial = async (testimonialData: Omit<AdminTestimonial, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
      const newTestimonial: AdminTestimonial = {
        ...testimonialData,
        id: `testimonial-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const updatedTestimonials = [...testimonials, newTestimonial];
      const updatedConfig = { ...testimonialConfig, testimonials: updatedTestimonials };
      await updateTestimonialConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add testimonial';
      setError(errorMessage);
      console.error('Error adding testimonial:', err);
      throw err;
    }
  };

  // Delete testimonial function
  const deleteTestimonial = async (testimonialId: string): Promise<void> => {
    try {
      const updatedTestimonials = testimonials.filter(testimonial => testimonial.id !== testimonialId);
      const updatedConfig = { ...testimonialConfig, testimonials: updatedTestimonials };
      await updateTestimonialConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete testimonial';
      setError(errorMessage);
      console.error('Error deleting testimonial:', err);
      throw err;
    }
  };

  return {
    testimonials,
    testimonialConfig,
    loading,
    error,
    refresh,
    updateTestimonial,
  addTestimonial,
  deleteTestimonial
  };
}; 

// Launch Splash Images Hook
interface UseLaunchSplashImagesResult {
  launchSplashImages: LaunchSplashImage[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createLaunchSplash: (params: {
    file: File;
    altText?: string;
    routePath?: string;
    createdBy?: string | null;
  }) => Promise<void>;
  updateLaunchSplashStatus: (imageId: string, status: "active" | "inactive") => Promise<void>;
  deleteLaunchSplash: (image: LaunchSplashImage) => Promise<void>;
}

export const useLaunchSplashImages = (): UseLaunchSplashImagesResult => {
  const [launchSplashImages, setLaunchSplashImages] = useState<LaunchSplashImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initializeLaunchSplash = async () => {
      try {
        setLoading(true);
        setError(null);

        unsubscribe = subscribeToLaunchSplashImages((images) => {
          setLaunchSplashImages(images);
          setLoading(false);
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load launch splash images";
        setError(errorMessage);
        setLoading(false);
        console.error("Error initializing launch splash images:", err);
      }
    };

    initializeLaunchSplash();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const refresh = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const images = await getLaunchSplashImages();
      setLaunchSplashImages(images);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to refresh launch splash images";
      setError(errorMessage);
      console.error("Error refreshing launch splash images:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createLaunchSplash = async (params: {
    file: File;
    altText?: string;
    routePath?: string;
    createdBy?: string | null;
  }): Promise<void> => {
    try {
      await createLaunchSplashImage(params);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create launch splash image";
      setError(errorMessage);
      console.error("Error creating launch splash image:", err);
      throw err;
    }
  };

  const updateLaunchSplashStatus = async (
    imageId: string,
    status: "active" | "inactive"
  ): Promise<void> => {
    try {
      await setLaunchSplashImageStatus(imageId, status);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update launch splash status";
      setError(errorMessage);
      console.error("Error updating launch splash status:", err);
      throw err;
    }
  };

  const deleteLaunchSplash = async (image: LaunchSplashImage): Promise<void> => {
    try {
      await deleteLaunchSplashImage(image);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete launch splash image";
      setError(errorMessage);
      console.error("Error deleting launch splash image:", err);
      throw err;
    }
  };

  return {
    launchSplashImages,
    loading,
    error,
    refresh,
    createLaunchSplash,
    updateLaunchSplashStatus,
    deleteLaunchSplash,
  };
};
